import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import PageHeader from "../components/PageHeader";
import { BULK_INVITE_WITH_USER_IDS } from "../gql/mutations/event";
import { RECORD_INVITES } from "../gql/mutations/outreach";
import { EVENTS_FOR_MEMBER } from "../gql/queries/eventMatching";
import { SHARE_EVENT_TO_EMAILS } from "../gql/queries/shareEvent";
import { ZONE_COLOURS, ZONE_ORDER } from "../helpers/const";
import { Spacing } from "../types/enum";
import { type ZoneLabel } from "../types/graphql";

/** How many top matches to pre-tick (matches the member tools). */
const PRE_TICK = 4;

interface Label {
  key: string;
  name: string;
  family: string;
}
interface EventForMember {
  eventId: string;
  title: string;
  labels: Label[];
  hostName?: string | null;
  hostBadge?: string | null;
  kind: string;
  zone: ZoneLabel;
  startsAt: string | number;
  localHour?: number | null;
  connectionScore: number;
  matchBasis: string;
  inHerZone: boolean;
  isFull: boolean;
  why: string[];
}

const ZoneChip: React.FC<{ zone?: ZoneLabel | null }> = ({ zone }) =>
  zone ? (
    <Chip
      size="small"
      label={zone}
      sx={{ backgroundColor: ZONE_COLOURS.get(zone), color: "#fff", fontWeight: 600 }}
    />
  ) : null;

const hourLabel = (hour: number): string => {
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h} ${hour < 12 ? "AM" : "PM"}`;
};

const FindEventsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [topicOnly, setTopicOnly] = useState(false);
  const [labelFilter, setLabelFilter] = useState<Set<string>>(new Set());
  const [zoneFilter, setZoneFilter] = useState<Set<string>>(new Set());
  const [kindFilter, setKindFilter] = useState<Set<string>>(new Set());

  const [lookup, { data, loading }] = useLazyQuery(EVENTS_FOR_MEMBER, {
    fetchPolicy: "network-only",
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    onCompleted: (result) => {
      // Pre-tick the top matches, unless she's over-contacted.
      const events = result?.adminEventsForMember?.matches ?? [];
      const overContacted = result?.adminEventsForMember?.member?.overContacted;
      setSelected(
        new Set(
          overContacted ? [] : events.slice(0, PRE_TICK).map((e: EventForMember) => e.eventId),
        ),
      );
      setTopicOnly(false);
      setLabelFilter(new Set());
      setZoneFilter(new Set());
      setKindFilter(new Set());
    },
  });

  const [shareEvent, { loading: sharing }] = useMutation(SHARE_EVENT_TO_EMAILS);
  const [recordInvites, { loading: recording }] = useMutation(RECORD_INVITES);
  const [inviteMember, { loading: inviting }] = useMutation(BULK_INVITE_WITH_USER_IDS);
  // Either request in flight disables both buttons, so a double click can't send twice.
  // Invite records to the outreach log after inviting, so that counts as in flight too.
  const busy = sharing || inviting || recording;

  const result = data?.adminEventsForMember;
  const member = result?.member;
  const allEvents: EventForMember[] = result?.matches ?? [];

  // Filter facets built from the events, like the member tools.
  const facets = useMemo(() => {
    const labels: Label[] = [];
    const seen = new Set<string>();
    const zones = new Set<string>();
    const kinds = new Set<string>();
    for (const event of allEvents) {
      for (const label of event.labels) {
        if (!seen.has(label.name)) {
          seen.add(label.name);
          labels.push(label);
        }
      }
      if (event.zone) zones.add(event.zone);
      if (event.kind) kinds.add(event.kind);
    }
    return {
      labels: labels.sort((a, b) => a.name.localeCompare(b.name)),
      zones: ZONE_ORDER.filter((z) => zones.has(z)),
      kinds: [...kinds].sort(),
    };
  }, [allEvents]);

  const visibleEvents = allEvents.filter((event) => {
    if (topicOnly && event.connectionScore <= 0) return false;
    if (zoneFilter.size && !zoneFilter.has(event.zone)) return false;
    if (kindFilter.size && !kindFilter.has(event.kind)) return false;
    if (labelFilter.size && !event.labels.some((l) => labelFilter.has(l.name))) return false;
    return true;
  });

  const onSearch = () => {
    if (!email.trim()) {
      enqueueSnackbar("Enter the member's email.", { variant: "info" });
      return;
    }
    void lookup({ variables: { email: email.trim(), days: 30, limit: 60 } });
  };

  const toggle = (set: Set<string>, value: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const selectAllVisible = () => {
    const allSelected = visibleEvents.every((e) => selected.has(e.eventId));
    setSelected(allSelected ? new Set() : new Set(visibleEvents.map((e) => e.eventId)));
  };

  const pickedEvents = allEvents.filter((e) => selected.has(e.eventId));

  const onShare = async () => {
    if (pickedEvents.length === 0 || !member) {
      enqueueSnackbar("Pick at least one event.", { variant: "info" });
      return;
    }
    let shared = 0;
    for (const event of pickedEvents) {
      try {
        const { data: r } = await shareEvent({
          variables: { eventId: event.eventId, emails: [member.email], source: "FIND_EVENTS" },
        });
        if (r?.adminShareEventToEmails?.sharedCount) shared += 1;
      } catch {
        /* per-event failure is reported in the total */
      }
    }
    enqueueSnackbar(`Shared ${shared} of ${pickedEvents.length} event(s) with ${member.name}.`, {
      variant: shared > 0 ? "success" : "warning",
    });
  };

  const onInvite = async () => {
    if (pickedEvents.length === 0 || !member) {
      enqueueSnackbar("Pick at least one event.", { variant: "info" });
      return;
    }
    let invited = 0;
    for (const event of pickedEvents) {
      try {
        await inviteMember({
          variables: { eventId: event.eventId, input: { userIds: [member.userId] } },
        });
        invited += 1;
        // Write the invite down. Inviting from here left no trace at all before
        // this, so the results board could see shares from Find Events and
        // nothing else — half the tool's work, invisible. Best-effort: the
        // invite already happened, and the log is a record, not a gate.
        try {
          await recordInvites({
            variables: {
              eventId: event.eventId,
              userIds: [member.userId],
              source: "FIND_EVENTS",
            },
          });
        } catch {
          /* the member is invited either way */
        }
      } catch (error) {
        enqueueSnackbar(
          error instanceof Error ? `${event.title}: ${error.message}` : "Invite failed.",
          { variant: "error" },
        );
      }
    }
    if (invited > 0) {
      enqueueSnackbar(`Invited ${member.name} to ${invited} event(s).`, { variant: "success" });
    }
  };

  // Her current local time, for eyeballing good hours.
  const herLocalNow = useMemo(() => {
    if (!member?.timezone) return "";
    try {
      return new Intl.DateTimeFormat("en-GB", {
        timeZone: member.timezone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(new Date());
    } catch {
      return "";
    }
  }, [member?.timezone]);

  const chipGroup = (
    title: string,
    values: string[],
    set: Set<string>,
    setter: (s: Set<string>) => void,
    labelFn: (v: string) => React.ReactNode = (v) => v,
  ) =>
    values.length > 0 && (
      <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
        <Typography variant="caption" color="textSecondary" sx={{ minWidth: 80 }}>
          {title}
        </Typography>
        {values.map((value) => (
          <Chip
            key={value}
            size="small"
            label={labelFn(value)}
            variant={set.has(value) ? "filled" : "outlined"}
            color={set.has(value) ? "primary" : "default"}
            onClick={() => toggle(set, value, setter)}
          />
        ))}
      </Box>
    );

  return (
    <>
      <PageHeader title="Find Events">
        Look up a member and see the upcoming events she'd enjoy — matched to her interests, goals,
        job and colour zone, in her own local time, with the ones she can't join left out.
      </PageHeader>

      <Card>
        <Box p={Spacing.l}>
          <Box display="flex" gap={Spacing.s} alignItems="flex-start" mb={Spacing.m}>
            <TextField
              label="Member email"
              placeholder="her@email.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && onSearch()}
              fullWidth
            />
            <Button variant="contained" onClick={onSearch} disabled={loading} sx={{ mt: 0.5 }}>
              {loading ? "Searching…" : "Search"}
            </Button>
          </Box>

          {member ? (
            <>
              {/* Member profile card */}
              <Box mb={Spacing.m}>
                <Box display="flex" alignItems="center" gap={Spacing.s} flexWrap="wrap">
                  <Typography variant="h5" color="textPrimary">
                    👤 {member.name}
                  </Typography>
                  <ZoneChip zone={member.zone} />
                  <Typography variant="body2" color="textSecondary">
                    · {member.email}
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" mt={Spacing.xs}>
                  🌍 {member.country ? `${member.country} · ` : ""}
                  {member.timezone ? member.timezone : "time zone unknown"}
                  {herLocalNow ? ` · ~${herLocalNow} her time now` : ""}
                </Typography>
                <Box
                  display="flex"
                  gap={Spacing.m}
                  flexWrap="wrap"
                  mt={Spacing.s}
                  alignItems="center"
                >
                  <Typography variant="body2">
                    <span style={{ opacity: 0.7 }}>Tenure:</span> {member.tenureBucket}
                  </Typography>
                  <Typography variant="body2">
                    <span style={{ opacity: 0.7 }}>Engagement:</span> {member.engagement}
                  </Typography>
                  <Typography variant="body2">
                    <span style={{ opacity: 0.7 }}>English:</span> {member.englishBand}
                  </Typography>
                  <Typography variant="body2">
                    <span style={{ opacity: 0.7 }}>Job:</span>{" "}
                    {member.occupation
                      ? `${member.occupation}${member.industry ? ` (${member.industry})` : ""}`
                      : "—"}
                  </Typography>
                  {member.isNewcomer && (
                    <Chip size="small" color="primary" label={`🆕 new (${member.tenureDays}d)`} />
                  )}
                  {member.upcomingBooked === 0 ? (
                    <Chip size="small" color="warning" label="⚠ no event booked" />
                  ) : (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${member.upcomingBooked} booked`}
                    />
                  )}
                  {member.overContacted ? (
                    <Chip
                      size="small"
                      color="warning"
                      label={`⚠ ${member.recentNotifs} invited/shared`}
                    />
                  ) : member.recentNotifs > 0 ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${member.recentNotifs} invited/shared`}
                    />
                  ) : (
                    <Chip size="small" variant="outlined" label="not yet invited/shared" />
                  )}
                </Box>
                <Box mt={Spacing.s}>
                  {member.interests.length > 0 || member.englishSkills.length > 0 ? (
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {member.interests.map((i: string) => (
                        <Chip key={i} size="small" color="primary" variant="outlined" label={i} />
                      ))}
                      {member.englishSkills.map((s: string) => (
                        <Chip key={s} size="small" color="secondary" variant="outlined" label={s} />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      no interests/goals on her profile — matched by job & colour zone
                    </Typography>
                  )}
                </Box>
              </Box>

              <Divider />

              {/* Filters */}
              <Stack spacing={Spacing.s} my={Spacing.m}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="caption" color="textSecondary" sx={{ minWidth: 80 }}>
                    Show
                  </Typography>
                  <Chip
                    size="small"
                    label="🎯 Topic matches only"
                    variant={topicOnly ? "filled" : "outlined"}
                    color={topicOnly ? "primary" : "default"}
                    onClick={() => setTopicOnly((v) => !v)}
                  />
                </Box>
                {chipGroup(
                  "Topic/Skill",
                  facets.labels.map((l) => l.name),
                  labelFilter,
                  setLabelFilter,
                )}
                {chipGroup("Zone", facets.zones, zoneFilter, setZoneFilter)}
                {chipGroup("Kind", facets.kinds, kindFilter, setKindFilter)}
              </Stack>

              <Box mb={Spacing.s}>
                <Typography variant="body2" color="textSecondary">
                  <strong>{result.topicMatchCount}</strong> topic match(es) +{" "}
                  <strong>{allEvents.length - result.topicMatchCount}</strong> also in her zone,
                  best first.
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {result.hiddenCantJoin} she can't join · {result.hiddenQuietHours} in her 11pm–7am
                  · {result.hiddenWrongLevel} at another English level
                  {result.hiddenPastNewcomerWindow
                    ? ` · ${result.hiddenPastNewcomerWindow} New Member Welcome${result.hiddenPastNewcomerWindow === 1 ? "" : "s"} (she's past her first 3 months)`
                    : ""}{" "}
                  — all hidden.
                </Typography>
                {!member.overContacted && allEvents.length > 0 && (
                  <Typography variant="caption" display="block" color="textSecondary">
                    Top {Math.min(PRE_TICK, allEvents.length)} pre-ticked — tick more or use “Select
                    all”.
                  </Typography>
                )}
              </Box>

              {visibleEvents.length === 0 ? (
                <Alert severity="info">No events match these filters.</Alert>
              ) : (
                <TableContainer sx={{ maxHeight: "55vh" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              visibleEvents.length > 0 &&
                              visibleEvents.every((e) => selected.has(e.eventId))
                            }
                            indeterminate={
                              visibleEvents.some((e) => selected.has(e.eventId)) &&
                              !visibleEvents.every((e) => selected.has(e.eventId))
                            }
                            onChange={selectAllVisible}
                          />
                        </TableCell>
                        <TableCell>Event</TableCell>
                        <TableCell>Zone / when (her time)</TableCell>
                        <TableCell>Host</TableCell>
                        <TableCell>Why she'd enjoy it</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleEvents.map((event) => (
                        <TableRow key={event.eventId} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selected.has(event.eventId)}
                              onChange={() => toggle(selected, event.eventId, setSelected)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{event.title}</Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                              {event.labels.length ? (
                                event.labels.map((label) => (
                                  <Chip
                                    key={label.key}
                                    label={label.name}
                                    size="small"
                                    variant="outlined"
                                    color={label.family === "skill" ? "secondary" : "primary"}
                                  />
                                ))
                              ) : (
                                <Typography variant="caption" color="textSecondary">
                                  no labels
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <ZoneChip zone={event.zone} />
                            <Typography variant="caption" display="block" color="textSecondary">
                              {format(new Date(event.startsAt), "EEE d MMM, HH:mm")} UTC
                            </Typography>
                            {event.localHour !== null && event.localHour !== undefined && (
                              <Typography variant="caption" display="block" color="textSecondary">
                                {hourLabel(event.localHour)} her time
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{event.hostName ?? "—"}</Typography>
                            {event.isFull && (
                              <Chip label="full" size="small" color="error" sx={{ ml: 0.5 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            {event.connectionScore > 0 ? (
                              <Stack spacing={0.25}>
                                {event.why.map((reason) => (
                                  <Typography key={reason} variant="caption" color="textPrimary">
                                    • {reason}
                                  </Typography>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                happening in her colour zone
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box display="flex" gap={Spacing.s} mt={Spacing.m}>
                <Button
                  variant="contained"
                  onClick={onShare}
                  disabled={busy || selected.size === 0}
                >
                  📤 Share selected with her ({selected.size})
                </Button>
                <Button
                  variant="outlined"
                  onClick={onInvite}
                  disabled={busy || selected.size === 0}
                >
                  📨 Invite her to selected
                </Button>
              </Box>
            </>
          ) : null}
        </Box>
      </Card>
    </>
  );
};

export default FindEventsPage;
